import type { EarlyWarning } from '@beam/core/sla';
import type { Status } from '@beam/core/status';
import type { MatterRow } from '@/app/types';

/** The saved views that replace the old routes (docs/PLAN.md Part 5), in strip order. */
export const PIPELINE_VIEWS = [
  { id: 'for-approval', label: 'For approval', statuses: [1] },
  { id: 'paperwork-outstanding', label: 'Paperwork outstanding', statuses: [2] },
  { id: 'generation-queue', label: 'Generation queue', statuses: [4, 5, 6] },
  { id: 'finance', label: 'Finance', statuses: [9, 10, 11, 12] },
  { id: 'awaiting-information', label: 'Awaiting information', statuses: [13] },
] as const satisfies ReadonlyArray<{ id: string; label: string; statuses: readonly Status[] }>;

export type ViewId = (typeof PIPELINE_VIEWS)[number]['id'];

export const TILES = [
  { id: 'overdue', label: () => 'overdue', tone: 'hot', warning: null, empty: 'No overdue cases.', description: () => 'Cases past the days allowed for their current status. Allowance from sla.json; placeholders until Beam confirms them.' },
  { id: 'paperwork', label: (t: Thresholds) => `paperwork not in, ${t.paperworkNotInDaysBeforeAppointment} days out`, tone: 'warning', warning: 'paperwork_not_in', empty: 'No cases with paperwork not in.', description: (t: Thresholds) => `Cases at 2 Paperwork Outstanding whose appointment is within ${t.paperworkNotInDaysBeforeAppointment} days. Threshold from sla.json; a placeholder until Beam confirms it.` },
  { id: 'drafts', label: (t: Thresholds) => `drafts due in ${t.draftDueWithinHoursNotStarted} hours, not started`, tone: 'warning', warning: 'draft_due_not_started', empty: 'No drafts due and not started.', description: (t: Thresholds) => `Cases at 6 Awaiting Doctor Draft due within ${t.draftDueWithinHoursNotStarted} hours with no draft started. Threshold from sla.json.` },
  { id: 'unconfirmed', label: (t: Thresholds) => `unconfirmed inside ${t.appointmentUnconfirmedWithinHours} hours`, tone: 'warning', warning: 'appointment_unconfirmed', empty: 'No unconfirmed appointments inside the window.', description: (t: Thresholds) => `Cases at 5 Awaiting Appointment whose appointment is within ${t.appointmentUnconfirmedWithinHours} hours and not yet confirmed. Threshold from sla.json.` },
] as const satisfies ReadonlyArray<{ id: string; label: (t: Thresholds) => string; tone: 'hot' | 'warning'; warning: EarlyWarning | null; empty: string; description: (t: Thresholds) => string }>;

export type TileId = (typeof TILES)[number]['id'];

export type Thresholds = { paperworkNotInDaysBeforeAppointment: number; draftDueWithinHoursNotStarted: number; appointmentUnconfirmedWithinHours: number };

export const DEFAULT_THRESHOLDS: Thresholds = { paperworkNotInDaysBeforeAppointment: 3, draftDueWithinHoursNotStarted: 48, appointmentUnconfirmedWithinHours: 72 };

export function tileMatches(id: TileId, row: MatterRow): boolean {
  if (id === 'overdue') return row.slaState === 'overdue';
  const tile = TILES.find((t) => t.id === id);
  return Boolean(tile?.warning && row.warnings.includes(tile.warning));
}

/** Reference (full or trailing digits) or patient as on the LOI; mirrors the server's matcher. */
export function matchesQuery(row: Pick<MatterRow, 'ref' | 'patientAsOnLoi'>, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if (/^\d+$/.test(needle)) return row.ref.replace(/\D/g, '').endsWith(needle);
  return row.ref.toLowerCase().includes(needle) || row.patientAsOnLoi.toLowerCase().includes(needle);
}

/** Beam's order (D-0066): Urgent first, then overdue, then oldest first by due date, then days in status descending. */
export function defaultOrder(a: MatterRow, b: MatterRow): number {
  const ua = a.flags.urgent ? 0 : 1;
  const ub = b.flags.urgent ? 0 : 1;
  if (ua !== ub) return ua - ub;
  const oa = a.slaState === 'overdue' ? 0 : 1;
  const ob = b.slaState === 'overdue' ? 0 : 1;
  if (oa !== ob) return oa - ob;
  if (a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
  return b.daysWaiting - a.daysWaiting;
}

export const DEFAULT_ORDER_WORDS = 'Urgent, then overdue, then oldest due date';

export const SORT_KEYS = ['due', 'days', 'matter', 'patient', 'referrer', 'doctor', 'status'] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const SORT_HINT: Record<SortKey, { asc: string; desc: string }> = {
  due: { asc: 'due date, soonest first', desc: 'due date, latest first' },
  days: { asc: 'days in status, fewest first', desc: 'days in status, most first' },
  matter: { asc: 'reference, A to Z', desc: 'reference, Z to A' },
  patient: { asc: 'patient, A to Z', desc: 'patient, Z to A' },
  referrer: { asc: 'referrer, A to Z', desc: 'referrer, Z to A' },
  doctor: { asc: 'doctor, A to Z', desc: 'doctor, Z to A' },
  status: { asc: 'status, 1 first', desc: 'status, 15 first' },
};
